import { type CreaAllegatoResponse } from '@framework/api-client/api/inquiries-compile/schemas/creaAllegatoResponse.ts';
import { type TipoComunicazioneFileAllegatoDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneFileAllegatoDTO.ts';
import { useField } from '@framework/form/field/useField.tsx';
import {
  UploadInput,
  type UploadOptions,
} from '@framework/form/inputs/upload/UploadInput.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { message, type UploadFile } from 'antd';
import { type RcFile, type UploadChangeParam } from 'antd/es/upload';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CreateCommunicationRequest } from '../../../schema/communication/CreateCommunicationRequestSchema.ts';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';
import { AttachmentDownloadIcon } from './AttachmentDownloadIcon.tsx';
import { AttachmentFromProcedureDialog } from './AttachmentFromProcedureDialog.tsx';
import { AttachmentGroupEditorContainer } from './AttachmentGroupEditorContainer.tsx';

/**
 * Costante per convertire i MB in byte.
 * 1 MB = 1024 * 1024 byte
 */
export const MB_TO_B = 1024 * 1024;

interface AttachmentGroupEditorProps {
  communication?: CreateCommunicationRequest | null;
  attachmentGroup: TipoComunicazioneFileAllegatoDTO;
  onUpload?: (file: RcFile) => Promise<CreaAllegatoResponse | null | undefined>;
  likeEmail?: boolean;
}

/**
 * Editor per la gestione degli allegati alla comunicazione (di uno specifico
 * gruppo di allegati).
 *
 * L'upload è asincrono rispetto al salvataggio: si effettua l'upload del file
 * e si salva l'id del file allegato nella comunicazione.
 *
 * Soltanto al salvataggio della comunicazione si avrà l'effettiva creazione
 * della relazione tra la comunicazione e l'allegato.
 */
export function AttachmentGroupEditor(props: AttachmentGroupEditorProps) {
  const { communication, attachmentGroup, onUpload, likeEmail } = props;

  const maxCount = attachmentGroup.numMaxFile ?? undefined;

  const { taskId } = useCommunicationsContext();

  const { setValue: setAttachments, getValue: getAttachments } =
    useField<CreateCommunicationRequest['allegati']>('allegati');

  // Creiamo una mappa per associare l'uid del file caricato con l'id del file
  // la mappa serve solo per i nuovi file (che vengono creati con un uid casuale)
  // mentre per quelli precedentemente caricati l'uid corrisponde all'id.
  // Non modifichiamo l'uid nella fileList direttamente per non avere problemi
  // di UI e rendering.
  const [fileUidMap, setFileUidMap] = useState<Record<string, string>>({});

  // Ids degli allegati già salvati nella comunicazione
  const savedAttachmentIds = useMemo(
    () =>
      new Set(
        communication?.allegati
          ?.map(attachment => attachment.id)
          .filter((id): id is string => !!id) ?? [],
      ),
    [communication?.allegati],
  );

  const acceptedExtensions = useMemo(
    () =>
      attachmentGroup.extFileConsentite
        ?.split(',')
        .map(ext => (ext.startsWith('.') ? ext : `.${ext}`))
        .join(',') ?? undefined,
    [attachmentGroup.extFileConsentite],
  );

  const { t } = useTranslation();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const parseFileList = useCallback(() => {
    const attachments =
      communication?.allegati?.filter(
        attachment =>
          attachment.tipoComunicazioneFileAllegato?.id === attachmentGroup.id,
      ) ?? [];

    return attachments.map(
      attachment =>
        ({
          name: attachment.nomeFile!,
          uid: attachment.id!,
          status: 'done',
        }) satisfies UploadFile,
    );
  }, [communication, attachmentGroup]);

  useEffect(() => {
    setFileList(parseFileList());
  }, [communication, parseFileList]);

  const handleChange = ({
    file,
    fileList,
  }: UploadChangeParam<UploadFile<any>>) => {
    if (file.status === 'error') {
      setFileList(fileList.filter(f => f.uid !== file.uid));
      return;
    }
    setFileList(fileList);
  };

  const handleUpload = (options: UploadOptions) => {
    const file = options.file as RcFile;
    async function upload() {
      try {
        const result = await onUpload?.(file);
        if (!result?.id) {
          // biome-ignore format: <tbd>
          throw new Error(t('communication-error.upload-no-id','Errore: Allegato creato senza ID.'));
        }
        options.onSuccess?.('ok');
        const list = getAttachments() ?? [];
        // Se l'upload è andato a buon fine, aggiorno i valori del form con
        // l'id del nuovo allegato
        setAttachments([...list, { id: result?.id }]);

        // Aggiorno la mappa per associare l'uid del file con l'id del file
        setFileUidMap(fileUidMap => ({
          ...fileUidMap,
          [file.uid]: result?.id ?? '',
        }));
      } catch (error) {
        options.onError?.(error as any);
      }
    }
    upload();
  };

  const handleRemove = useCallback(
    (file: UploadFile) => {
      const newFileList = fileList.filter(
        f => f.uid !== (fileUidMap[file.uid] ?? file.uid),
      );

      const list = getAttachments() ?? [];
      // Imposta il file come "uploading" per visualizzare il loader
      setFileList(newFileList);

      const newAttachmentList = list.filter(
        // Controllo l'id dalla mappa. Se non è presente, il file è già stato salvato
        // e quindi posso usare il suo uid come id.
        a => a.id != (fileUidMap[file.uid] ?? file.uid),
      );
      setAttachments(newAttachmentList);

      return true;
    },
    [fileList, fileUidMap, getAttachments, setAttachments],
  );

  const isDisabled = maxCount != null && fileList.length >= maxCount;

  const renderDownloadIcon = useCallback(
    (file: UploadFile) => (
      <AttachmentDownloadIcon
        file={file}
        fileUidMap={fileUidMap}
        savedAttachmentIds={savedAttachmentIds}
      />
    ),
    [fileUidMap, savedAttachmentIds],
  );

  return (
    <AttachmentGroupEditorContainer
      likeEmail={likeEmail}
      title={
        attachmentGroup.denominazione ??
        t(
          'communication-attachment-group-editor.title-generic-fallback',
          'Allegato',
        )
      }
    >
      <FlexRow
        align="flex-start"
        right={
          taskId && (
            <AttachmentFromProcedureDialog
              attachmentGroup={attachmentGroup}
              setFileList={setFileList}
              disabled={isDisabled}
            />
          )
        }
      >
        <UploadInput
          title={t(
            'communication-attachment-group-editor.upload-file',
            `Carica file {{maxCount}}`,
            {
              maxCount:
                maxCount != null
                  ? t(
                      'common:communication-attachment-group-editor.max-upload-file',
                      '(Massimo {{maxCount}})',
                      { maxCount },
                    )
                  : '',
            },
          )}
          fileList={fileList}
          maxCount={maxCount}
          multiple
          showUploadList={{
            showDownloadIcon: true,
            downloadIcon: renderDownloadIcon,
          }}
          accept={acceptedExtensions}
          buttonProps={{
            style: {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              zIndex: 1,
            },
            disabled: isDisabled,
          }}
          customRequest={handleUpload}
          onRemove={handleRemove}
          onChange={handleChange}
          // Gestiamo la dimensione massima del file
          beforeUpload={async (inputFile: RcFile, fileList: RcFile[]) => {
            const maxFileSize = attachmentGroup.dimMax ?? 0;
            if (maxFileSize > 0 && inputFile.size > maxFileSize * MB_TO_B) {
              console.warn(`Il file ${inputFile.name} è troppo grande`);
              // biome-ignore format: <tbd>
              message.error(`Il file ${inputFile.name} è troppo grande. (Dimensione massima: ${maxFileSize} MB)`);
              return false;
            }
            return true;
          }}
        />
      </FlexRow>
    </AttachmentGroupEditorContainer>
  );
}
